import { APP_PAGE_KEYS } from "../appConfig/appConfig";
import { resolveMystudioApiOrigin } from "../config/localPreview";

export const APP_MANIFEST_PATH = "/api/v4/public/app-manifest";
export const APP_PAGE_PATH_PREFIX = "/api/v4/public/pages";

export function getAppConfigOrigin() {
  return resolveMystudioApiOrigin();
}

export function getAppClientVersion() {
  return String(process.env.REACT_APP_LATEST_APP_VERSION || "").trim();
}

function addRequestContext(url, {
  officeId,
  clientVersion,
  device,
} = {}) {
  const normalizedOfficeId = Number(officeId);
  if (Number.isInteger(normalizedOfficeId) && normalizedOfficeId > 0) {
    url.searchParams.set("officeId", String(normalizedOfficeId));
  }
  const normalizedDevice = String(device || "").trim().toLowerCase();
  if (["mobile", "tablet", "desktop"].includes(normalizedDevice)) {
    url.searchParams.set("device", normalizedDevice);
  }
  const normalizedClientVersion = String(clientVersion || "").trim();
  if (normalizedClientVersion) {
    url.searchParams.set("clientVersion", normalizedClientVersion);
  }
  return url;
}

export function buildAppManifestUrl(origin, officeId, requestContext = {}) {
  const normalizedOrigin = String(origin || "").trim().replace(/\/+$/, "");
  if (!normalizedOrigin) return "";
  return addRequestContext(
    new URL(`${normalizedOrigin}${APP_MANIFEST_PATH}`),
    { ...requestContext, officeId },
  ).toString();
}

export function buildAppPageUrl(origin, pageKey, officeId, requestContext = {}) {
  const normalizedOrigin = String(origin || "").trim().replace(/\/+$/, "");
  const normalizedPageKey = String(pageKey || "").trim().toLowerCase();
  if (!normalizedOrigin || !Object.values(APP_PAGE_KEYS).includes(normalizedPageKey)) return "";

  return addRequestContext(
    new URL(`${normalizedOrigin}${APP_PAGE_PATH_PREFIX}/${encodeURIComponent(normalizedPageKey)}`),
    { ...requestContext, officeId },
  ).toString();
}

async function requestAppConfig(url, { signal, fetchImpl = globalThis.fetch } = {}) {
  if (!url) {
    return {
      configured: false,
      payload: null,
      etag: "",
    };
  }
  if (typeof fetchImpl !== "function") throw new Error("App configuration fetch is unavailable.");

  const response = await fetchImpl(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
    signal,
  });

  if (!response.ok) {
    throw new Error(`App configuration request failed with status ${response.status}.`);
  }

  return {
    configured: true,
    payload: await response.json(),
    etag: response.headers?.get?.("etag") || "",
  };
}

export function requestAppManifest({
  origin = getAppConfigOrigin(),
  officeId,
  clientVersion = getAppClientVersion(),
  device,
  signal,
  fetchImpl,
} = {}) {
  return requestAppConfig(
    buildAppManifestUrl(origin, officeId, { clientVersion, device }),
    { signal, fetchImpl },
  );
}

export function requestAppPage({
  origin = getAppConfigOrigin(),
  pageKey,
  officeId,
  clientVersion = getAppClientVersion(),
  device,
  signal,
  fetchImpl,
} = {}) {
  return requestAppConfig(
    buildAppPageUrl(origin, pageKey, officeId, { clientVersion, device }),
    { signal, fetchImpl },
  );
}
